// src/pages/AssignTrainings.jsx
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/* ----------------------------------------------------
   Hybrid Zoho + Keka Style - Assign Trainings Page
   ----------------------------------------------------
   Features:
   ✔ Poppins + Roboto Fonts
   ✔ Employee List + Search
   ✔ Training Catalogue (with categories, duration, cost-INR)
   ✔ Training Assignment Modal (compact, non-header-overlay)
   ✔ LocalStorage persistence
   ✔ Dummy Data Included
   ✔ Clean UI, responsive
------------------------------------------------------ */

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap');`;

const LS_KEY = "training_assignments_v1";

// Dummy Employees
const EMPLOYEES = [
  { id: "E1001", name: "Aman Sharma", role: "Frontend Developer", dept: "IT" },
  { id: "E1002", name: "Neha Gupta", role: "HR Executive", dept: "HR" },
  { id: "E1003", name: "Vikram Rao", role: "Backend Engineer", dept: "IT" },
  { id: "E1004", name: "Riya Singh", role: "Sales Manager", dept: "Sales" },
  { id: "E1005", name: "Suresh Kumar", role: "Finance Analyst", dept: "Finance" },
];

// Dummy Trainings
const TRAININGS = [
  { id: "T001", title: "React Advanced Workshop", category: "Technical", duration: "12 hrs", cost: 3500 },
  { id: "T002", title: "Leadership Essentials", category: "Management", duration: "8 hrs", cost: 0 },
  { id: "T003", title: "Payroll Compliance (India)", category: "HR", duration: "6 hrs", cost: 2500 },
  { id: "T004", title: "Communication Skills", category: "Soft Skills", duration: "4 hrs", cost: 0 },
  { id: "T005", title: "Advanced Node.js", category: "Technical", duration: "10 hrs", cost: 4000 },
];

// Modal Component
const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(0,0,0,0.45)" }}
      >
        <motion.div
          initial={{ y: -5 }} animate={{ y: 0 }} exit={{ y: -5 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[75vh] overflow-y-auto"
        >
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button onClick={onClose} className="px-3 py-1 rounded hover:bg-slate-100">✕</button>
          </div>
          <div className="p-4">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default function AssignTrainings() {
  const [assignments, setAssignments] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [searchEmp, setSearchEmp] = useState("");
  const [searchTraining, setSearchTraining] = useState("");

  const [selectedEmp, setSelectedEmp] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [dueDate, setDueDate] = useState("");

  // Persist
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(assignments));
  }, [assignments]);

  const filteredEmployees = useMemo(() => {
    return EMPLOYEES.filter(
      (e) =>
        e.name.toLowerCase().includes(searchEmp.toLowerCase()) ||
        e.role.toLowerCase().includes(searchEmp.toLowerCase())
    );
  }, [searchEmp]);

  const filteredTrainings = useMemo(() => {
    return TRAININGS.filter(
      (t) =>
        t.title.toLowerCase().includes(searchTraining.toLowerCase()) ||
        t.category.toLowerCase().includes(searchTraining.toLowerCase())
    );
  }, [searchTraining]);

  const openAssignPopup = (emp) => {
    setSelectedEmp(emp);
    setSelectedTraining(null);
    setDueDate("");
    setAssignModalOpen(true);
  };

  const assignTraining = () => {
    if (!selectedTraining || !dueDate) {
      alert("Select training & due date");
      return;
    }

    const newItem = {
      id: `A_${Date.now()}`,
      employeeId: selectedEmp.id,
      trainingId: selectedTraining.id,
      dueDate,
      assignedAt: new Date().toISOString(),
      status: "Assigned",
    };

    setAssignments((prev) => [newItem, ...prev]);
    setAssignModalOpen(false);
  };

  const getTraining = (id) => TRAININGS.find((t) => t.id === id);
  const getEmployee = (id) => EMPLOYEES.find((e) => e.id === id);

  return (
    <div className="p-6 bg-slate-50 min-h-screen" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <style>{FONT_IMPORT}</style>

      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Assign Trainings</h1>
          <p className="text-slate-500 text-sm">Assign technical, behavioural & compliance trainings to employees.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Employees */}
          <div className="bg-white p-4 rounded-xl shadow-sm h-[70vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold">Employees</h2>
            </div>

            <input
              type="text"
              placeholder="Search employee..."
              value={searchEmp}
              onChange={(e) => setSearchEmp(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-3"
            />

            {filteredEmployees.map((emp) => (
              <div
                key={emp.id}
                className="p-3 bg-slate-50 rounded-lg flex justify-between items-center mb-2 hover:bg-indigo-50 cursor-pointer"
              >
                <div>
                  <div className="font-medium">{emp.name}</div>
                  <div className="text-xs text-slate-500">{emp.role}</div>
                </div>
                <button
                  onClick={() => openAssignPopup(emp)}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Assign
                </button>
              </div>
            ))}
          </div>

          {/* Middle Panel - Trainings */}
          <div className="bg-white p-4 rounded-xl shadow-sm h-[70vh] overflow-y-auto lg:col-span-1">
            <h2 className="text-lg font-semibold mb-3">Training Catalogue</h2>

            <input
              type="text"
              placeholder="Search training..."
              value={searchTraining}
              onChange={(e) => setSearchTraining(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4"
            />

            {filteredTrainings.map((t) => (
              <div key={t.id} className="p-3 border rounded-lg mb-2 bg-white">
                <div className="font-medium text-slate-800">{t.title}</div>
                <div className="text-xs text-slate-500 mt-1">{t.category}</div>
                <div className="text-xs text-slate-500">Duration: {t.duration}</div>
                <div className="text-xs text-slate-600">
                  Cost: {t.cost > 0 ? `₹ ${t.cost.toLocaleString("en-IN")}` : "Free"}
                </div>
              </div>
            ))}
          </div>

          {/* Right Panel - Assigned Trainings */}
          <div className="bg-white p-4 rounded-xl shadow-sm h-[70vh] overflow-y-auto lg:col-span-1">
            <h2 className="text-lg font-semibold mb-3">Assigned Trainings</h2>

            {assignments.length === 0 && (
              <div className="text-center text-slate-500 mt-10">No trainings assigned yet.</div>
            )}

            {assignments.map((a) => {
              const emp = getEmployee(a.employeeId);
              const tr = getTraining(a.trainingId);

              return (
                <div key={a.id} className="p-3 border rounded-lg mb-3 bg-slate-50">
                  <div className="font-medium">{tr.title}</div>
                  <div className="text-xs text-slate-500">Employee: {emp.name}</div>
                  <div className="text-xs text-slate-500">Due: {a.dueDate}</div>
                  <div className="text-xs text-emerald-600 font-medium mt-1">{a.status}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <Modal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title={`Assign Training - ${selectedEmp?.name}`}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-600">Select Training</label>
            <select
              value={selectedTraining?.id || ""}
              onChange={(e) => setSelectedTraining(TRAININGS.find(t => t.id === e.target.value))}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="">-- Select --</option>
              {TRAININGS.map((t) => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-slate-600">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full border rounded px-3 py-2 mt-1"
            />
          </div>

          <div className="pt-3 flex justify-end gap-2">
            <button className="px-4 py-2 bg-white border rounded" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </button>
            <button className="px-4 py-2 bg-indigo-600 text-white rounded" onClick={assignTraining}>
              Assign
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
